type ApiErrorShape = {
  status?: number;
  data?: unknown;
  error?: string;
};

const isApiErrorShape = (error: unknown): error is ApiErrorShape =>
  typeof error === 'object' && error !== null && 'status' in error;

type SerializedError = {
  message?: string;
  name?: string;
  stack?: string;
};

const isSerializedError = (error: unknown): error is SerializedError =>
  typeof error === 'object' &&
  error !== null &&
  'message' in error &&
  typeof (error as { message?: unknown }).message === 'string';

const getSingleApiErrorMessage = (error: unknown, fallbackKey: string): string => {
  if (isApiErrorShape(error)) {
    if ('data' in error && error.data) {
      const data = error.data;
      if (typeof data === 'string') {
        if (data === 'target must be an object') {
          return fallbackKey;
        }
        return data;
      }
      if (
        typeof data === 'object' &&
        data !== null &&
        'detail' in data &&
        typeof (data as { detail?: unknown }).detail === 'string'
      ) {
        return (data as { detail: string }).detail;
      }
      if (
        typeof data === 'object' &&
        data !== null &&
        'errorTranslationCode' in data &&
        typeof (data as { errorTranslationCode?: unknown }).errorTranslationCode === 'string'
      ) {
        return (data as { errorTranslationCode: string }).errorTranslationCode;
      }
      if (
        typeof data === 'object' &&
        data !== null &&
        'errors' in data &&
        typeof (data as { errors?: unknown }).errors === 'object' &&
        (data as { errors: unknown }).errors !== null &&
        'generalErrors' in (data as { errors: { generalErrors?: unknown } }).errors
      ) {
        const errs = (data as { errors: { generalErrors?: unknown } }).errors;
        const generalErrors = errs.generalErrors;
        if (Array.isArray(generalErrors) && generalErrors.length > 0) {
          const first = generalErrors.find(
            (item): item is string => typeof item === 'string' && item.trim().length > 0
          );
          if (first !== undefined) {
            return first;
          }
        }
        if (typeof generalErrors === 'string' && generalErrors.trim().length > 0) {
          return generalErrors;
        }
      }
      if (
        typeof data === 'object' &&
        data !== null &&
        'message' in data &&
        typeof (data as { message?: unknown }).message === 'string'
      ) {
        return (data as { message: string }).message;
      }
    }
    if ('error' in error && typeof error.error === 'string') {
      return error.error;
    }
  }
  if (isSerializedError(error)) {
    return error.message ?? fallbackKey;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallbackKey;
};

export const extractApiErrorMessages = (
  error: unknown,
  fallbackKey = 'system.unknown'
): string[] => {
  if (isApiErrorShape(error)) {
    if ('data' in error && error.data) {
      const data = error.data;
      if (typeof data === 'object' && data !== null && 'errors' in data) {
        const errs = (data as { errors?: unknown }).errors;
        if (errs && typeof errs === 'object' && 'generalErrors' in errs) {
          const generalErrors = (errs as { generalErrors?: unknown }).generalErrors;
          if (Array.isArray(generalErrors) && generalErrors.length > 0) {
            const strings = generalErrors.filter(
              (item): item is string => typeof item === 'string' && item.trim().length > 0
            );
            if (strings.length > 0) {
              return strings;
            }
          }
        }
      }
    }
  }
  const single = getSingleApiErrorMessage(error, fallbackKey);
  return single ? [single] : [];
};

export const extractApiErrorMessage = (error: unknown, fallbackKey = 'system.unknown'): string => {
  const messages = extractApiErrorMessages(error, fallbackKey);
  if (messages.length === 0) {
    return fallbackKey;
  }
  return messages.length > 1 ? messages.join('\n') : messages[0];
};

export const extractRtkQueryErrorMessages = extractApiErrorMessages;
export const extractRtkQueryErrorMessage = extractApiErrorMessage;
